import { i18n } from '@fs/zion-locale'
import { languageConfig, searchConfig } from '../../config'

// IETF language tags combine subtags from other standards such as ISO 639, ISO 15924, ISO 3166-1 and UN M.49.
// We are currently only concerned with the country code ISO 639-1 and 639-2

const displayNamesOf = (translateTo, code, type = 'language') => {
  if (typeof Intl === 'undefined' || typeof Intl.DisplayNames === 'undefined') {
    return code
  }

  const name = new Intl.DisplayNames([translateTo], { type }).of(code)

  // this will handle some languages that are not supported by the Intl API
  if (type === 'language' && name === code) {
    // we want the language name in the language of the language; that's not confusing...
    if (translateTo === code && languageConfig.languageTranslations[code]) {
      return languageConfig.languageTranslations[code]
    }

    // because we are using i18n.t instead of useTranslation, we need to have it in the render pipeline
    const translatedLanguagesMap = {
      ase: i18n.t('global.asl', 'American Sign Language'),
      pon: i18n.t('global.pohnpeian', 'Pohnpeian'),
    }
    // we want the language name in the language of the user
    if (translatedLanguagesMap[code]) {
      return translatedLanguagesMap[code]
    }
  }

  return name
}

/**
 * @param {String} code the language code to get the name of
 * @returns the language name in the users language
 */
const getLanguageName = (code) => {
  const userCode = i18n.language
  return displayNamesOf(userCode, code)
}

/**
 * Retrieves the region name based on the provided code.
 *
 * @param {string} code - The code representing the region.
 * @returns {string} The display name of the region.
 */
const getRegionName = (code) => {
  const userCode = i18n.language
  return displayNamesOf(userCode, code, 'region')
}

/**
 * @param {String} code the language code to get the name of
 * @returns the language name in the provided language
 */
const getNativeLanguageName = (code) => {
  try {
    const languageName = displayNamesOf(code, code)
    return languageName.charAt(0).toLocaleUpperCase(code) + languageName.slice(1)
  } catch {
    return ''
  }
}

/**
 * @param {String} code ISO 639 code
 * @returns The language code that BS can read; RFC 5646 {language}-{country/region}
 */
const getSearchLanguageCode = (code) => {
  return searchConfig.languageWhiteList.find((value) => value.includes(code))
}

const exceptions = ['zh-HK']
/**
 * @param {String} code language code
 * @returns ISO 639-2 code (if we have it mapped)
 */
const getISO639_2LanguageCode = (code = '') => {
  let langCode = code

  // it's a BSP code that we can convert to a two letter code
  if (!exceptions.includes(code) && code.length > 2) {
    langCode = code.slice(0, 2)
  }

  const fallback = languageConfig.iso639Conversions.en
  return languageConfig.iso639Conversions[langCode] ?? fallback
}

const isUsersLocale = (BSLocale) => {
  if (BSLocale === 'zh-CN') {
    return i18n.language === 'zh-hans'
  }
  if (i18n.language === BSLocale.split('-').shift()) {
    return true
  }
  return false
}

const getBrowserSeoLocales = (alternateLocales = []) => {
  return alternateLocales
    .map((locale) => {
      if (languageConfig.chineseLocales.includes(locale)) {
        return languageConfig.chineseLangMapping[locale]
      }
      return locale.split('-')[0]
    })
    .filter((locale) => locale !== i18n.language)
}

/**
 * Normalizes a locale string into an SEO-safe `hreflang` value.
 *
 * Rules:
 * - Accepts language-only values (`en`) and language+variant values (`en_US`, `zh-hans`).
 * - Converts underscores to hyphens, trims empty segments, and ignores malformed structures.
 * - Applies configured language and region aliases before validation.
 * - Supports script variants (`zh-Hans`) and region variants (`en-US`).
 * - If the region variant is invalid, falls back to the normalized language.
 *
 * @param {string} locale Raw locale from CMS/content (for example: `en_US`, `eng-uk`, `zh-hans`).
 * @returns {string | null} A normalized locale suitable for `hreflang` (for example: `en-US`, `zh-Hans`, `fr`), or `null` when the input cannot be normalized.
 */
const normalizeHreflangLocale = (locale = '') => {
  if (typeof locale !== 'string' || locale.length === 0) {
    return null
  }

  const localeParts = locale.replaceAll('_', '-').split('-').filter(Boolean)

  if (localeParts.length === 0 || localeParts.length > 2) {
    return null
  }

  const [rawLanguage = '', rawVariant = ''] = localeParts
  const language = languageConfig.hreflangLanguageAliases[rawLanguage.toLowerCase()] ?? rawLanguage.toLowerCase()

  if (!/^[a-z]{2,3}$/.test(language)) {
    return null
  }

  if (!rawVariant) {
    return language
  }

  if (/^[a-z]{4}$/i.test(rawVariant)) {
    const script = `${rawVariant.charAt(0).toUpperCase()}${rawVariant.slice(1).toLowerCase()}`
    return `${language}-${script}`
  }

  const aliasedRegion = languageConfig.hreflangRegionAliases[rawVariant.toLowerCase()]
  const region = aliasedRegion ?? rawVariant.toUpperCase()

  if (!/^[A-Z]{2}$/.test(region)) {
    return language
  }

  return `${language}-${region}`
}

const getHreflangLocales = ({ contentLocale, alternateLocales = [] } = {}) => {
  return [...new Set([contentLocale, ...alternateLocales].map(normalizeHreflangLocale).filter(Boolean))]
}

const getProspectiveLanguages = () => {
  return languageConfig.additionalLangs.filter((_, i) => i !== 0)
}

/**
 * gets the browsers language even if we do not support it in our app
 * @returns the language in the format that frontier uses
 */
const getBrowserLanguage = () => {
  if (navigator.language === 'zh-CN') {
    return 'zh-hans'
  }

  return navigator.language.slice(0, 2)
}

export {
  getLanguageName,
  getNativeLanguageName,
  getSearchLanguageCode,
  getISO639_2LanguageCode,
  getBrowserSeoLocales,
  getHreflangLocales,
  isUsersLocale,
  getProspectiveLanguages,
  getBrowserLanguage,
  getRegionName,
}
